import { useContext, useState } from "react";
import styles from "./CommonFields.module.css";
import {
    Collapsible,
    Divider,
    FormLayout,
    Link,
    RadioButton,
    TextField,
    Label,
    Banner,
    LegacyCard,
    TextContainer,
    Box,
    Tabs,
    BlockStack,
    Text,
    Card,
    Tooltip,
    Icon,
    InlineStack,
    Select,
    InlineGrid,
} from "@shopify/polaris";
import { MdOutlineArrowBackIosNew } from "react-icons/md";
import { Field } from "@shopify/react-form";
import { AppConfigContext } from "../../../context/AppConfigContext";
import { ActiveDatesCard } from "@shopify/discount-app-components";
import useBundleMixpanelHook from "../../../hooks/useBundleMixpanelHook";
import BundleEmbed from "../../../components/BundleEmbed/BundleEmbed";
import { getAppBlockDeepLink } from "./WidgetPositionsSelector";
import { AvailableFBTTemplates, BundleTypes, DefaultWidgetPositions } from "../constants";
import HideNativeElement from "./HideNativeElement";
import { ProductObj } from "../Types";
import SettingRow, { tabs } from "./SettingRow";
import { InfoIcon } from "@shopify/polaris-icons";

type Props_Additional = {
    dispatchBundleConfig: any;
    submit: (event?: React.FormEvent) => Promise<void>;
    start_date: Field<string>;
    end_date: Field<string | null>;
    add_to_cart_cta_text: Field<string>;
    bundleType: string;
    bundle_embed_id?: any;
    default_cta?: any;
    bundle_position?: any;
    custom_selector?: any;
    limit_max_quantity_per_product?: Field<boolean>;
    max_quantity_per_product?: Field<number>;
    render_embed_on_visibility?: Field<boolean>;
    hide_native_elements?: any;
    show_unit_price?: any;
    use_compare_at_price?: any;
    apply_discount_on_compare_price?: any;
    prevent_deselect_main_product?: Field<boolean>;
    no_preselect_offered_products?: Field<boolean>;
    new_bundle_position?: any;
    new_custom_selector?: any;
    bundleConfig?: any;
    is_cta_above?: Field<boolean>;
    allow_radio_selection?: Field<boolean>;
    remove_addons_if_main_missing?: Field<boolean>;
    show_prices?: Field<boolean>;
    disable_product_title_link?: Field<boolean>;
    price_display_type: Field<string>;
    should_flatten_products?: Field<boolean>;
    products?: Field<ProductObj[]>;
    discounted_products?: Field<ProductObj[]>;
    show_free_gift_popper?: Field<boolean>;
    hide_out_of_stock_products?: Field<boolean>;
    disable_atc_until_threshold?: Field<boolean>;
    atc_threshold?: Field<number>;
    atc_tooltip_text?: Field<string>;
    bundle_layout?: Field<string>;
};

const Additional = ({
    dispatchBundleConfig,
    submit,
    start_date,
    end_date,
    bundleType,
    bundle_embed_id,
    limit_max_quantity_per_product,
    max_quantity_per_product,
    render_embed_on_visibility,
    hide_native_elements,
    show_unit_price,
    use_compare_at_price,
    apply_discount_on_compare_price,
    prevent_deselect_main_product,
    no_preselect_offered_products,
    bundleConfig,
    is_cta_above,
    allow_radio_selection,
    remove_addons_if_main_missing,
    show_prices,
    disable_product_title_link,
    price_display_type,
    should_flatten_products,
    products,
    discounted_products,
    show_free_gift_popper,
    hide_out_of_stock_products,
    disable_atc_until_threshold,
    atc_threshold,
    atc_tooltip_text,
    bundle_layout,
}: Props_Additional) => {
    const [expandPosition, setExpandPosition] = useState(true);
    const [expandPricingSection, setExpandPricingSection] = useState(false);
    const [expandSchedule, setExpandSchedule] = useState(false);
    const [selectedTab, setSelectedTab] = useState(0);

    const visibleTabs = tabs.filter((tab) => tab.bundles.includes(bundleType));

    const handleTabChange = (selectedTabIndex: number) => setSelectedTab(selectedTabIndex);

    const currentTabId = visibleTabs[selectedTab]?.id;

    const appConfigContext = useContext(AppConfigContext);
    const { trackFormSections } = useBundleMixpanelHook();

    const defaultPosition =
        bundleType == BundleTypes.FBT && bundleConfig?.selected_template_fbt === AvailableFBTTemplates.VERTICAL
            ? DefaultWidgetPositions[BundleTypes.STANDARD]
            : DefaultWidgetPositions[bundleType];

    return (
        <>
            {![BundleTypes.PRODUCT_ADDONS_POPUP, BundleTypes.BUNDLE_BUILDER]?.includes(bundleType as any) && (
                <>
                    <div
                        className={styles.sectionTitleWrapper}
                        onClick={() =>
                            setExpandPosition((ps) => {
                                trackFormSections({
                                    cta: "Form Section",
                                    ctaType: `Additional ${ps ? "Collapsed" : "Expanded"}`,
                                    bundleType: bundleType,
                                });
                                return !ps;
                            })
                        }
                    >
                        <div>Widget Position</div>

                        <div
                            style={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                transform: expandPosition ? "rotate(90deg)" : "rotate(270deg)",
                            }}
                        >
                            <MdOutlineArrowBackIosNew size={14} />
                        </div>
                    </div>
                    <Collapsible
                        open={expandPosition}
                        id="basic-collapsible"
                        transition={{
                            duration: "500ms",
                            timingFunction: "ease-in-out",
                        }}
                        expandOnPrint
                    >
                        <FormLayout>
                            {BundleTypes.PRODUCT_ADDONS && is_cta_above && (
                                <>
                                    <div className={styles.customLabelWrapper} style={{ margin: "5px 0px" }}>
                                        <Label id="bundle_position"> Bundle Position:</Label>
                                    </div>
                                    <div className={`${styles.formCol}`}>
                                        <RadioButton
                                            label="Show bundle below Add to Cart"
                                            checked={is_cta_above.value === true}
                                            id="cta-above"
                                            name="ctaPosition"
                                            onChange={() => {
                                                dispatchBundleConfig({
                                                    payload: true,
                                                    key: "is_cta_above",
                                                });
                                                is_cta_above.onChange(true);
                                            }}
                                        />

                                        <RadioButton
                                            label="Show bundle above Add to Cart"
                                            checked={is_cta_above.value === false}
                                            id="cta-below"
                                            name="ctaPosition"
                                            onChange={() => {
                                                dispatchBundleConfig({
                                                    payload: false,
                                                    key: "is_cta_above",
                                                });
                                                is_cta_above.onChange(false);
                                            }}
                                        />
                                    </div>
                                </>
                            )}
                            <div style={{ display: "flex", flexDirection: "column", gap: 10, lineHeight: "20px" }}>
                                <div>
                                    {bundleConfig.bundle_type !== BundleTypes.PRODUCT_ADDONS ? (
                                        <>
                                            By default, the widget will appear <b>{defaultPosition}</b>. <br /> To
                                            change its position, open the &nbsp;
                                            <Link
                                                url={getAppBlockDeepLink({
                                                    shop: appConfigContext.shop,
                                                    extensionId: appConfigContext.extensionId,
                                                    bundleType,
                                                })}
                                                external
                                            >
                                                Shopify Theme Editor
                                            </Link>
                                            ,&nbsp;and drag the app block to your preferred location.
                                        </>
                                    ) : (
                                        <>
                                            <LegacyCard>
                                                <TextContainer>
                                                    <Banner tone="info">
                                                        To change the widget placement on the page, open the &nbsp;
                                                        <Link
                                                            url={getAppBlockDeepLink({
                                                                shop: appConfigContext.shop,
                                                                extensionId: appConfigContext.extensionId,
                                                                bundleType,
                                                            })}
                                                            external
                                                        >
                                                            Shopify Theme Editor
                                                        </Link>
                                                        ,&nbsp;and drag the app block to your preferred location.
                                                        <br />
                                                        <Link
                                                            external
                                                            url={
                                                                "https://bundles-help.keficommerce.com/faq/what-s-the-best-way-to-position-bundles-on-my-product-pages"
                                                            }
                                                        >
                                                            Need help?
                                                        </Link>
                                                    </Banner>
                                                </TextContainer>
                                            </LegacyCard>
                                        </>
                                    )}
                                </div>

                                {bundleConfig.bundle_type !== BundleTypes.PRODUCT_ADDONS && (
                                    <div>
                                        <Link
                                            external
                                            url={
                                                "https://bundles-help.keficommerce.com/faq/what-s-the-best-way-to-position-bundles-on-my-product-pages"
                                            }
                                        >
                                            Need help?
                                        </Link>
                                    </div>
                                )}
                            </div>
                            <Divider />
                            {bundle_embed_id && (
                                <BundleEmbed bundleType={bundleType} bundle_embed_id={bundle_embed_id} />
                            )}
                            {hide_native_elements && (
                                <HideNativeElement
                                    bundleType={bundleType}
                                    dispatchBundleConfig={dispatchBundleConfig}
                                    hide_native_elements={hide_native_elements}
                                    submit={submit}
                                />
                            )}
                        </FormLayout>
                    </Collapsible>
                    <div className={styles.dividerStyle}>
                        <Divider borderColor="border" />
                    </div>
                </>
            )}
            {visibleTabs?.length > 0 ? (
                <>
                    <div
                        className={styles.sectionTitleWrapper}
                        onClick={() =>
                            setExpandPricingSection((ps) => {
                                trackFormSections({
                                    cta: "Form Section",
                                    ctaType: `Additional ${ps ? "Collapsed" : "Expanded"}`,
                                    bundleType: bundleType,
                                });
                                return !ps;
                            })
                        }
                    >
                        <div>Product Settings</div>

                        <div
                            style={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                transform: expandPricingSection ? "rotate(90deg)" : "rotate(270deg)",
                            }}
                        >
                            <MdOutlineArrowBackIosNew size={14} />
                        </div>
                    </div>
                    <Collapsible
                        open={expandPricingSection}
                        id="basic-collapsible"
                        transition={{
                            duration: "500ms",
                            timingFunction: "ease-in-out",
                        }}
                        expandOnPrint
                    >
                        <Box>
                            <Tabs tabs={visibleTabs} selected={selectedTab} onSelect={handleTabChange}>
                                <Box padding="400" paddingBlockStart="0">
                                    <Card>
                                        {currentTabId === "visibility" && (
                                            // @ts-ignore - BlockStack is not recognizing the class name
                                            <BlockStack className={styles.settingsBlockStack}>
                                                {hide_out_of_stock_products && (
                                                    <SettingRow
                                                        label="Hide Out of Stock Products"
                                                        description="Automatically hide products that are out of stock."
                                                        tooltipContent="Do not show items that are out of stock in the bundle."
                                                        checked={hide_out_of_stock_products.value}
                                                        onChange={(val: any) => {
                                                            dispatchBundleConfig({
                                                                payload: val,
                                                                key: "hide_out_of_stock_products",
                                                            });
                                                            hide_out_of_stock_products.onChange(val);
                                                        }}
                                                    />
                                                )}
                                                {show_free_gift_popper && (
                                                    <SettingRow
                                                        label="Show popper animation for free gifts"
                                                        checked={show_free_gift_popper.value}
                                                        onChange={(val) => {
                                                            dispatchBundleConfig({
                                                                payload: val,
                                                                key: "show_free_gift_popper",
                                                            });
                                                            show_free_gift_popper.onChange(val);
                                                        }}
                                                    />
                                                )}
                                                {should_flatten_products && (
                                                    <SettingRow
                                                        label="Display each quantity as a separate card"
                                                        description="When enabled, multiple quantities of a product will be shown as separate cards instead of a single card with a higher quantity"
                                                        checked={should_flatten_products.value}
                                                        onChange={(val) => {
                                                            dispatchBundleConfig({
                                                                payload: val,
                                                                key: "should_flatten_products",
                                                            });
                                                            should_flatten_products.onChange(val);
                                                        }}
                                                        disabled={
                                                            products?.value?.every((p) => p?.quantity == 1) &&
                                                            discounted_products?.value?.every((p) => p?.quantity == 1)
                                                        }
                                                        tooltipContent={
                                                            products?.value?.every((p) => p?.quantity == 1) &&
                                                            discounted_products?.value?.every((p) => p?.quantity == 1)
                                                                ? "Disabled because all product quantities are set to 1."
                                                                : null
                                                        }
                                                    />
                                                )}

                                                {bundle_layout && (
                                                    <Box paddingBlock="400">
                                                        <BlockStack gap="200">
                                                            <InlineGrid
                                                                columns={{ xs: "1fr auto" }}
                                                                alignItems="center"
                                                                gap="400"
                                                            >
                                                                <BlockStack gap="100">
                                                                    <Text
                                                                        as="span"
                                                                        variant="bodyMd"
                                                                        fontWeight="semibold"
                                                                    >
                                                                        Layout
                                                                    </Text>
                                                                    <Text as="p" variant="bodyMd" tone="subdued">
                                                                        Choose how product cards are displayed.
                                                                    </Text>
                                                                </BlockStack>
                                                                <div style={{ minWidth: "150px" }}>
                                                                    <Select
                                                                        label=""
                                                                        labelHidden
                                                                        options={[
                                                                            { label: "Stacked", value: "stacked" },
                                                                            { label: "Carousel", value: "carousel" },
                                                                        ]}
                                                                        value={bundle_layout?.value}
                                                                        onChange={(val: string) => {
                                                                            bundle_layout?.onChange(val);
                                                                            dispatchBundleConfig({
                                                                                payload: val,
                                                                                key: "bundle_layout",
                                                                            });
                                                                        }}
                                                                    />
                                                                </div>
                                                            </InlineGrid>
                                                        </BlockStack>
                                                    </Box>
                                                )}
                                            </BlockStack>
                                        )}

                                        {currentTabId === "pricing" && (
                                            // @ts-ignore - BlockStack is not recognizing the class name
                                            <BlockStack className={styles.settingsBlockStack}>
                                                {bundleType !== BundleTypes.STANDARD && use_compare_at_price && (
                                                    <>
                                                        <SettingRow
                                                            label="Display ‘Compare at’ price"
                                                            description="Show the Compare-at price in the product card."
                                                            checked={use_compare_at_price.value}
                                                            onChange={(val) => {
                                                                dispatchBundleConfig({
                                                                    payload: val,
                                                                    key: "use_compare_at_price",
                                                                });
                                                                use_compare_at_price.onChange(val);
                                                            }}
                                                            disabled={apply_discount_on_compare_price?.value}
                                                        />
                                                    </>
                                                )}
                                                {apply_discount_on_compare_price && (
                                                    <SettingRow
                                                        label="Apply discount on the Compare-at Price"
                                                        description="Calculate discount based on Compare-at Price instead of selling price."
                                                        tooltipContent={
                                                            <div>
                                                                <p>
                                                                    Your discount will be calculated based on the
                                                                    product’s Compare-at Price instead of the actual
                                                                    selling price.
                                                                </p>
                                                                <p>Important Note</p>
                                                                Your discount must be{" "}
                                                                <b>
                                                                    greater than the discount Shopify already shows
                                                                    &nbsp;
                                                                </b>
                                                                between the Compare-at Price and the Actual Price.
                                                                <p>
                                                                    <b>Example:</b>
                                                                </p>
                                                                <ul>
                                                                    <li>Compare-at Price: $100</li>
                                                                    <li>Actual Price: $90</li>
                                                                    <li>
                                                                        → Shopify is already showing a 10% discount.
                                                                    </li>
                                                                </ul>
                                                                <p>
                                                                    To apply your custom discount correctly, you must
                                                                    set a discount
                                                                    <b>&nbsp;greater than 10% in app</b>
                                                                </p>
                                                            </div>
                                                        }
                                                        checked={apply_discount_on_compare_price.value}
                                                        onChange={(val) => {
                                                            dispatchBundleConfig({
                                                                payload: val,
                                                                key: "apply_discount_on_compare_price",
                                                            });
                                                            apply_discount_on_compare_price.onChange(val);
                                                            if (val && use_compare_at_price) {
                                                                dispatchBundleConfig({
                                                                    payload: val,
                                                                    key: "use_compare_at_price",
                                                                });
                                                                use_compare_at_price.onChange(val);
                                                            }
                                                        }}
                                                    />
                                                )}
                                                {show_unit_price && (
                                                    <>
                                                        <SettingRow
                                                            label="Show unit prices"
                                                            checked={show_unit_price.value}
                                                            onChange={(val) => {
                                                                dispatchBundleConfig({
                                                                    payload: val,
                                                                    key: "show_unit_price",
                                                                });
                                                                show_unit_price.onChange(val);
                                                            }}
                                                        />
                                                    </>
                                                )}
                                                {show_prices && (
                                                    <>
                                                        <SettingRow
                                                            label="Show prices in card when product is not added"
                                                            checked={show_prices.value}
                                                            onChange={(val) => {
                                                                dispatchBundleConfig({
                                                                    payload: val,
                                                                    key: "show_prices",
                                                                });
                                                                show_prices.onChange(val);
                                                                if (val && !price_display_type?.value) {
                                                                    dispatchBundleConfig({
                                                                        payload: "both_price_and_compare_at_price",
                                                                        key: "price_display_type",
                                                                    });
                                                                    price_display_type?.onChange(
                                                                        "both_price_and_compare_at_price",
                                                                    );
                                                                }
                                                            }}
                                                            hideDivider={true}
                                                        />
                                                        {show_prices.value && price_display_type && (
                                                            <Box paddingInlineStart="400" paddingBlockEnd="400">
                                                                <BlockStack gap="200">
                                                                    <RadioButton
                                                                        label="Show both product price and compare at price"
                                                                        checked={
                                                                            price_display_type.value ===
                                                                            "both_price_and_compare_at_price"
                                                                        }
                                                                        id="price-both"
                                                                        name="priceDisplayType"
                                                                        onChange={() => {
                                                                            dispatchBundleConfig({
                                                                                payload:
                                                                                    "both_price_and_compare_at_price",
                                                                                key: "price_display_type",
                                                                            });
                                                                            price_display_type.onChange(
                                                                                "both_price_and_compare_at_price",
                                                                            );
                                                                        }}
                                                                    />

                                                                    <RadioButton
                                                                        label="Show only product price"
                                                                        checked={
                                                                            price_display_type.value === "price_only"
                                                                        }
                                                                        id="price-product-only"
                                                                        name="priceDisplayType"
                                                                        onChange={() => {
                                                                            dispatchBundleConfig({
                                                                                payload: "price_only",
                                                                                key: "price_display_type",
                                                                            });
                                                                            price_display_type.onChange("price_only");
                                                                        }}
                                                                    />
                                                                </BlockStack>
                                                            </Box>
                                                        )}
                                                    </>
                                                )}
                                            </BlockStack>
                                        )}

                                        {currentTabId === "selection" && (
                                            // @ts-ignore - BlockStack is not recognizing the class name
                                            <BlockStack className={styles.settingsBlockStack}>
                                                {prevent_deselect_main_product && (
                                                    <SettingRow
                                                        label="Allow customers to deselect the main product"
                                                        checked={!prevent_deselect_main_product.value}
                                                        onChange={(val) => {
                                                            dispatchBundleConfig({
                                                                payload: !val,
                                                                key: "prevent_deselect_main_product",
                                                            });
                                                            prevent_deselect_main_product.onChange(!val);
                                                        }}
                                                    />
                                                )}
                                                {no_preselect_offered_products && (
                                                    <>
                                                        <SettingRow
                                                            label="Do not preselect the items in the bundle"
                                                            checked={no_preselect_offered_products.value}
                                                            onChange={(val) => {
                                                                dispatchBundleConfig({
                                                                    payload: val,
                                                                    key: "no_preselect_offered_products",
                                                                });
                                                                no_preselect_offered_products.onChange(val);
                                                            }}
                                                            disabled={
                                                                allow_radio_selection && allow_radio_selection.value
                                                            }
                                                        />
                                                    </>
                                                )}
                                                {remove_addons_if_main_missing && (
                                                    <>
                                                        <SettingRow
                                                            label="Automatically remove add-on products when the main product is removed from the cart"
                                                            checked={remove_addons_if_main_missing.value}
                                                            onChange={(val) => {
                                                                dispatchBundleConfig({
                                                                    payload: val,
                                                                    key: "remove_addons_if_main_missing",
                                                                });
                                                                remove_addons_if_main_missing.onChange(val);
                                                            }}
                                                        />
                                                    </>
                                                )}
                                                {allow_radio_selection && (
                                                    <>
                                                        <SettingRow
                                                            label="Allow customers to select only one add-on product"
                                                            checked={allow_radio_selection.value}
                                                            onChange={(val) => {
                                                                dispatchBundleConfig({
                                                                    payload: val,
                                                                    key: "allow_radio_selection",
                                                                });
                                                                allow_radio_selection.onChange(val);
                                                            }}
                                                        />
                                                    </>
                                                )}

                                                {limit_max_quantity_per_product && max_quantity_per_product && (
                                                    <>
                                                        <SettingRow
                                                            label="Max Quantity Selection Per Product"
                                                            checked={limit_max_quantity_per_product.value}
                                                            onChange={(val) => {
                                                                dispatchBundleConfig({
                                                                    payload: val,
                                                                    key: "limit_max_quantity_per_product",
                                                                });
                                                                limit_max_quantity_per_product.onChange(val);
                                                            }}
                                                        />
                                                        {limit_max_quantity_per_product.value && (
                                                            <Box paddingBlockStart="200" paddingBlockEnd="400">
                                                                <TextField
                                                                    label="Max Quantity"
                                                                    labelHidden
                                                                    onBlur={() => {
                                                                        if (max_quantity_per_product?.value <= 0) {
                                                                            dispatchBundleConfig({
                                                                                payload: 1,
                                                                                key: "max_quantity_per_product",
                                                                            });
                                                                            max_quantity_per_product.onChange(1);
                                                                        }
                                                                    }}
                                                                    placeholder={""}
                                                                    autoComplete="off"
                                                                    type="number"
                                                                    min={0}
                                                                    max={20}
                                                                    onChange={(val) => {
                                                                        let numVal = +val;
                                                                        if (numVal > 20) {
                                                                            numVal = 20;
                                                                        }
                                                                        dispatchBundleConfig({
                                                                            payload: +numVal,
                                                                            key: "max_quantity_per_product",
                                                                        });
                                                                        max_quantity_per_product.onChange(+numVal);
                                                                    }}
                                                                    value={max_quantity_per_product?.value?.toString()}
                                                                />
                                                            </Box>
                                                        )}
                                                    </>
                                                )}
                                                {disable_atc_until_threshold && atc_threshold && (
                                                    <>
                                                        <SettingRow
                                                            label="Disable the Add to Cart button until threshold reached"
                                                            tooltipContent="Prevent customers from adding the bundle to cart until they have selected a minimum number of items."
                                                            checked={disable_atc_until_threshold.value}
                                                            onChange={(val) => {
                                                                dispatchBundleConfig({
                                                                    payload: val,
                                                                    key: "disable_atc_until_threshold",
                                                                });
                                                                disable_atc_until_threshold.onChange(val);
                                                            }}
                                                            hideDivider={true}
                                                        />
                                                        {disable_atc_until_threshold.value && (
                                                            <Box paddingBlockStart="200" paddingBlockEnd="400">
                                                                <TextField
                                                                    label="Threshold Quantity"
                                                                    onBlur={() => {
                                                                        if (atc_threshold?.value <= 0) {
                                                                            dispatchBundleConfig({
                                                                                payload: 1,
                                                                                key: "atc_threshold",
                                                                            });
                                                                            atc_threshold.onChange(1);
                                                                        }
                                                                    }}
                                                                    placeholder={""}
                                                                    autoComplete="off"
                                                                    type="number"
                                                                    min={1}
                                                                    onChange={(val) => {
                                                                        dispatchBundleConfig({
                                                                            payload: +val,
                                                                            key: "atc_threshold",
                                                                        });
                                                                        atc_threshold.onChange(+val);
                                                                    }}
                                                                    value={atc_threshold?.value?.toString()}
                                                                />
                                                                {atc_tooltip_text && (
                                                                    <Box paddingBlockStart="300">
                                                                        <Box paddingBlockEnd="100">
                                                                            <InlineStack gap="100" align="start">
                                                                                <Text as="span" variant="bodyMd">
                                                                                    Tooltip Message
                                                                                </Text>

                                                                                <Tooltip
                                                                                    content="Use {itemCount} as a placeholder for the threshold quantity"
                                                                                    width="wide"
                                                                                >
                                                                                    <Icon
                                                                                        source={InfoIcon}
                                                                                        tone="subdued"
                                                                                    />
                                                                                </Tooltip>
                                                                            </InlineStack>
                                                                        </Box>

                                                                        <TextField
                                                                            label="Tooltip Message"
                                                                            labelHidden
                                                                            autoComplete="off"
                                                                            onChange={(val) => {
                                                                                dispatchBundleConfig({
                                                                                    payload: val,
                                                                                    key: "atc_tooltip_text",
                                                                                });
                                                                                atc_tooltip_text.onChange(val);
                                                                            }}
                                                                            value={atc_tooltip_text.value}
                                                                        />
                                                                    </Box>
                                                                )}
                                                            </Box>
                                                        )}
                                                    </>
                                                )}
                                            </BlockStack>
                                        )}

                                        {currentTabId === "flow-rules" && (
                                            // @ts-ignore - BlockStack is not recognizing the class name
                                            <BlockStack className={styles.settingsBlockStack}>
                                                {disable_product_title_link && (
                                                    <SettingRow
                                                        label="Disable redirection to product page"
                                                        description="Prevent customers from being redirected when clicking a product inside the bundle."
                                                        checked={disable_product_title_link.value}
                                                        onChange={(val) => {
                                                            dispatchBundleConfig({
                                                                payload: val,
                                                                key: "disable_product_title_link",
                                                            });
                                                            disable_product_title_link.onChange(val);
                                                        }}
                                                    />
                                                )}
                                                {!disable_product_title_link && (
                                                    <Box padding="400">
                                                        <Text as="p" tone="subdued">
                                                            No flow or rules available for this bundle type.
                                                        </Text>
                                                    </Box>
                                                )}
                                            </BlockStack>
                                        )}
                                    </Card>
                                </Box>
                            </Tabs>
                        </Box>
                    </Collapsible>
                    <div className={styles.dividerStyle}>
                        <Divider borderColor="border" />
                    </div>
                </>
            ) : null}

            <div
                className={styles.sectionTitleWrapper}
                onClick={() =>
                    setExpandSchedule((ps) => {
                        trackFormSections({
                            cta: "Form Section",
                            ctaType: `Additional ${ps ? "Collapsed" : "Expanded"}`,
                            bundleType: bundleType,
                        });
                        return !ps;
                    })
                }
            >
                <div>Scheduling</div>

                <div
                    style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        transform: expandSchedule ? "rotate(90deg)" : "rotate(270deg)",
                    }}
                >
                    <MdOutlineArrowBackIosNew size={14} />
                </div>
            </div>
            <Collapsible
                open={expandSchedule}
                id="basic-collapsible"
                transition={{
                    duration: "500ms",
                    timingFunction: "ease-in-out",
                }}
                expandOnPrint
            >
                <FormLayout>
                    <div style={{ marginBottom: "-1rem", marginTop: -3 }}>
                        {appConfigContext?.iana_timezone && (
                            <ActiveDatesCard
                                startDate={{
                                    value: start_date.value,
                                    onChange: (val) => {
                                        dispatchBundleConfig({
                                            payload: val,
                                            key: "start_date",
                                        });
                                        start_date.onChange(val);
                                    },
                                }}
                                endDate={{
                                    value: end_date.value,
                                    onChange: (val) => {
                                        dispatchBundleConfig({
                                            payload: val,
                                            key: "end_date",
                                        });
                                        end_date.onChange(val);
                                    },
                                }}
                                timezoneAbbreviation={appConfigContext?.iana_timezone || ""}
                            />
                        )}
                    </div>
                </FormLayout>
            </Collapsible>
            <div className={styles.dividerStyle}>
                <Divider borderColor="border" />
            </div>
        </>
    );
};

export default Additional;
